import { InkPoint } from '../models/draft-form.model';

/** Default epsilon for normalized 0-1 coordinates */
export const RDP_EPSILON = 0.002;

/**
 * Ramer-Douglas-Peucker stroke simplification.
 * Reduces point count by ~80% while preserving visual fidelity.
 * Works on normalized 0-1 coordinates.
 */
export function simplifyStroke(
  points: InkPoint[],
  epsilon: number = RDP_EPSILON
): InkPoint[] {
  if (points.length <= 2) {
    return points;
  }

  const start = points[0];
  const end = points[points.length - 1];
  let maxDist = 0;
  let maxIdx = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], start, end);
    if (dist > maxDist) {
      maxDist = dist;
      maxIdx = i;
    }
  }

  if (maxDist > epsilon) {
    const left = simplifyStroke(points.slice(0, maxIdx + 1), epsilon);
    const right = simplifyStroke(points.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }

  return [start, end];
}

function perpendicularDistance(
  point: InkPoint,
  lineStart: InkPoint,
  lineEnd: InkPoint
): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    return Math.hypot(point.x - lineStart.x, point.y - lineStart.y);
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lenSq
    )
  );

  const projX = lineStart.x + t * dx;
  const projY = lineStart.y + t * dy;

  return Math.hypot(point.x - projX, point.y - projY);
}
